import React, { useEffect, useState } from "react";
import TabEmplacement from "./Tabs/TabEmplacement";
import TabRestaurant from "./Tabs/TabRestaurant";
import TabTenant from "./Tabs/TabTenant";
import TabWebTime from "./Tabs/TabWebTime";
import CreateLocationModal from "./Modal/CreateLocationModal";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Box,
  TextField,
  FormControl,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "../../css/Emplacement.css";

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`location-tabpanel-${index}`}
      aria-labelledby={`location-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `location-tab-${index}`,
    "aria-controls": `location-tabpanel-${index}`,
  };
}

function LocationView() {
  const { t } = useTranslation(["Location"]);
  const userState = useSelector((state) => state.user);

  const [restaurants, setRestaurants] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [currentRestaurant, setCurrentRestaurant] = useState(null);

  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [regions, setRegions] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [alignment, setAlignment] = useState("web");
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  // Snackbar alert state
  const [alert, setAlert] = useState({
    show: false,
    type: "success",
    message: "",
  });

  useEffect(() => {
    const currentId = localStorage.getItem("currentRestaurantId");
    const savedTab = localStorage.getItem("tabValue");

    const init = async () => {
      await getCountriesProvincesRegions();
      const data = await getEmplacement();

      if (currentId) {
        const resto = data.find((r) => r.noEmplacement == currentId);
        if (resto) {
          setRestaurant(resto.nom);
          setCurrentRestaurant(resto);
        }
        localStorage.removeItem("currentRestaurantId");
      }

      if (savedTab) {
        setTabValue(parseInt(savedTab, 10));
        localStorage.removeItem("tabValue");
      }
    };

    init();
  }, []);

  const getCountriesProvincesRegions = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Localization/GetProvince`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        }
      );
      const data = await response.json();

      const uniqueCountries = [];
      const uniqueProvinces = [];
      const uniqueRegions = [];

      data.forEach((element) => {
        if (!uniqueCountries.find((c) => c.nomFr === element.paysFr))
          uniqueCountries.push({
            id: uniqueCountries.length + 1,
            nomFr: element.paysFr,
            nomEn: element.paysEn,
          });
        if (!uniqueProvinces.find((p) => p.nomFr === element.provinceFr))
          uniqueProvinces.push({
            id: uniqueProvinces.length + 1,
            nomFr: element.provinceFr,
            nomEn: element.provinceEn,
          });
        if (!uniqueRegions.find((r) => r.nomFr === element.regionFr))
          uniqueRegions.push({
            id: uniqueRegions.length + 1,
            nomFr: element.regionFr,
            nomEn: element.regionEn,
          });
      });

      setCountries(uniqueCountries);
      setProvinces(uniqueProvinces);
      setRegions(uniqueRegions);
    } catch (error) {
      console.error("Error fetching location data:", error);
      setAlert({ show: true, type: "error", message: t("ErrorFetchingData") });
    } finally {
      setIsLoading(false);
    }
  };

  const getEmplacement = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/Location/GetEmplacement`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userState.token}`,
        },
      }
    );
    const data = await response.json();
    setRestaurants(data);
    return data;
  };

  const handleChangeEmplacement = (event, newValue) => {
    if (newValue) {
      const selectedRestaurant = restaurants.find((r) => r.nom === newValue);
      setCurrentRestaurant(selectedRestaurant);
      setRestaurant(newValue);
    } else {
      setCurrentRestaurant(null);
      setRestaurant(null);
    }
  };

  const handleChangeToggleButton = (event, newAlignment) => {
    setAlignment(newAlignment);
  };

  const saveLocationData = async (data, userToken, activeTab) => {
    const parseDate = (dateString) => {
      if (!dateString) return null;
      const [day, month, year] = dateString.split("-");
      return dayjs(`${year}-${month}-${day}`);
    };

    let locationData = {
      noEmplacement: Number(data.noEmplacement) || 0,
      noAdresse: data.noAdresse || 0,
      activeTab,
    };

    if (activeTab === 0) {
      const dateOuverture = parseDate(data.dateOuverture);
      const dateFermeture = parseDate(data.dateFermeture);

      locationData = {
        ...locationData,
        nom: data.nameLocation || "",
        email: data.email || null,
        noTelPrincipal: data.phoneNumber || null,
        dateOuverture: dateOuverture
          ? dateOuverture.format("YYYY-MM-DDTHH:mm:ss")
          : null,
        dateFermeture: dateFermeture
          ? dateFermeture.format("YYYY-MM-DDTHH:mm:ss")
          : null,
        adresseEmplacement: {
          adresse: data.address || "",
          ville: data.city || "",
          codePostal: data.zipCode || "",
          nomRegionFr: data.region?.nomFr || "",
          nomProvinceFr: data.province?.nomFr || "",
          nomPaysFr: data.country?.nomFr || "",
          nomRegionEn: data.region?.nomEn || "",
          nomProvinceEn: data.province?.nomEn || "",
          nomPaysEn: data.country?.nomEn || "",
        },
      };
    }
    // 🟢 RESTAURANT TAB
if (activeTab === 1) {
  locationData = {
    ...locationData,
    nbSiege: data.nbSiege,
    superficie: data.superficie,
    ipAddress: data.IpAddress,
    prixLivraison: data.PrixLivraison,
    prixLivraisonParPalette: data.PrixLivraisonParPalette,
    idTypePaiement: data.IdTypePaiement,
    nomTypePaiementFr: data.NomTypePaiementFr,
  };
}

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Location/SaveLocation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
          body: JSON.stringify(locationData),
        }
      );

      const result = await response.json();

      if (response.ok) {
        // Affiche le Snackbar
        setAlert({
          show: true,
          type: "success",
          message: "Location saved successfully!",
        });

        // Attend 1,5 seconde pour que l'utilisateur voit l'alerte
        setTimeout(() => {
          localStorage.setItem("currentRestaurantId", data.noEmplacement);
          localStorage.setItem("tabValue", activeTab);
          window.location.reload();
        }, 1500);
      } else {
        setAlert({
          show: true,
          type: "error",
          message: result.title || "Error saving location",
        });
      }
    } catch (error) {
      console.error(error);
      setAlert({
        show: true,
        type: "error",
        message: "Error connecting to server",
      });
    }
  };

  const handleTabChange = (event, newValue) => setTabValue(newValue);
  const handleCloseAlert = () => setAlert((prev) => ({ ...prev, show: false }));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          onClick={() => setOpenModal(true)}
          sx={{ textTransform: "none" }}
        >
          {t("NewLocation")}
        </Button>
        <ToggleButtonGroup
          color="primary"
          value={alignment}
          exclusive
          onChange={handleChangeToggleButton}
          aria-label="Platform"
        >
          <ToggleButton value={true}>{t("ShowInactiveLocation")}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <CreateLocationModal
        open={openModal}
        handleClose={() => setOpenModal(false)}
        countries={countries}
        provinces={provinces}
        regions={regions}
      />

      <Box sx={{ minWidth: 120, mb: 3 }}>
        <FormControl fullWidth>
          <Autocomplete
            options={restaurants.map((r) => r.nom)}
            onChange={handleChangeEmplacement}
            value={restaurant || null}
            renderInput={(params) => (
              <TextField {...params} label={t("Location")} />
            )}
          />
        </FormControl>
      </Box>

      <Box>
        {!isLoading && countries && provinces && regions ? (
          <Box sx={{ backgroundColor: "white", borderRadius: 1, p: 3 }}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Tabs value={tabValue} onChange={handleTabChange} centered>
                <Tab label={t("Location")} {...a11yProps(0)} />
                <Tab label={t("Restaurant")} {...a11yProps(1)} />
                <Tab label={t("Tenant")} {...a11yProps(2)} />
                <Tab label={t("WebTime")} {...a11yProps(3)} />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              <TabEmplacement
                emplacement={currentRestaurant}
                countries={countries}
                provinces={provinces}
                regions={regions}
                onSave={saveLocationData}
              />
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <TabRestaurant
                emplacement={currentRestaurant}
                onSave={saveLocationData}
              />
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              <TabTenant
                emplacement={currentRestaurant}
                onSave={saveLocationData}
              />
            </TabPanel>
            <TabPanel value={tabValue} index={3}>
              <TabWebTime
                emplacement={currentRestaurant}
                onSave={saveLocationData}
              />
            </TabPanel>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>

      {/* Snackbar alert */}
      <Snackbar
        open={alert.show}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.type}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LocationView;
